import React, { FunctionComponent, useMemo, useState } from 'react';
import { useAsync } from 'react-async-hook';
import classNames from 'classnames';
import { Dealer } from '../../types/dealerTypes';
// eslint-disable-next-line import/no-cycle
import { getActiveDealerLaunchSections } from '../DealerLaunchDashboard/steps';
import { StatusList } from '../StatusList/StatusList';
import UserService from '../../services/UserService';
import { Spinner } from '../Spinner/Spinner';
import { DealerLaunchStep } from '../DealerLaunchDashboard/types';
import WidgetService from '../../services/WidgetService';
import { LinkButton } from '../Input/Button/LinkButton';
import { CHAIRMAN_URL } from '../../utils/routes';
import FormTemplateService from '../../services/FormTemplateService';
import styles from './styles.module.scss';

interface Props {
  dealer: Dealer;
  useChairmanLinks?: boolean;
}

export const DealerStepSummary: FunctionComponent<Props> = ({
  dealer,
  useChairmanLinks,
}) => {
  const users = useAsync(UserService.getAll, [dealer.id]);
  const widgetConfig = useAsync(WidgetService.getConfig, [dealer.id]);
  const formTemplates = useAsync(FormTemplateService.getAll, [dealer.id]);

  const sections = useMemo(() => getActiveDealerLaunchSections(dealer), [
    dealer,
  ]);
  const [currentSection, setCurrentSection] = useState(sections[0]);

  const renderStepReviewData = (step: DealerLaunchStep, index: number) => {
    const reviewData = step.getReviewData?.(dealer, {
      users: users.result?.data,
      widgetConfig: widgetConfig.result?.data,
      forms: formTemplates.result?.data,
    });
    if (!reviewData?.length) return null;

    return (
      <div key={index} className={styles.reviewData}>
        <div className={styles.reviewDataName}>
          {step.name}
          <LinkButton
            className={styles.reviewDataNameEditButton}
            label="Edit"
            to={
              useChairmanLinks
                ? step.getChairmanUrl?.(dealer) || CHAIRMAN_URL
                : step.route.getPath(dealer)
            }
            variant="minimal"
            rawLink={useChairmanLinks}
            openNewWindow={useChairmanLinks}
          />
        </div>
        <div className={styles.reviewDataContent}>
          {reviewData.map(
            ({ name, value, nameClassName, valueClassName }, subIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={subIndex}>
                <div
                  className={classNames(
                    styles.reviewDataContentLabel,
                    nameClassName,
                  )}
                >
                  {name}
                </div>
                <div
                  className={classNames(
                    value === null && styles.reviewDataContentEmpty,
                    valueClassName,
                  )}
                >
                  {value === null ? 'Not provided' : value}
                </div>
              </React.Fragment>
            ),
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {(users.loading || widgetConfig.loading || formTemplates.loading) && (
        <Spinner variant="fill-container-overlay" />
      )}
      <div className={styles.sectionListContainer}>
        <div className={styles.header}>All Sections</div>
        <div>
          <StatusList
            items={sections
              .filter((section) => !section.hideInReviewSummary)
              .map((section) => ({
                label: section.name,
                className:
                  section === currentSection
                    ? styles.activeSectionItem
                    : styles.inactiveSectionItem,
                complete: section.isComplete(dealer),
                onClick: () => setCurrentSection(section),
              }))}
          />
        </div>
      </div>
      <div className={styles.sectionDataContainer}>
        <div className={styles.header}>
          What we have in this section so far:
        </div>
        <div className={styles.sectionData}>
          {currentSection.steps.map(renderStepReviewData)}
        </div>
      </div>
    </div>
  );
};
